import { mapValues, omit } from 'es-toolkit';

import {
  appendDateAndTimeToFileName,
  getUserVisibleOrDefaultExceptionMessage,
  sanitizeFilename,
  saveTextContent,
} from '@paragon/client-tools';
import {
  ComparePlateListsRequest,
  ComponentDesign,
  ConvertibleComponentFileExtension,
  DEFAULT_TRUSS_DESIGN_DRAWING_OPTIONS,
  DigitalQCOutputFormat,
  DownloadMachineryTrsRequest,
  DownloadMaterialReportRequest,
  DownloadRequest,
  PlateManufacturer,
  PlatePair,
  SharedComponentSet,
  ShopDrawingOptions,
  TrussDesignDrawingOptions,
} from '@paragon/contract-library';
import {
  designService,
  DownloadableFileResponse,
  downloadDigitalQcFile,
  downloadQualityControlDiagrams,
  EmailResponse,
  NoFileContentResponse,
} from '@paragon/data-connections';
import { SegmentForLevel } from '@paragon/data-connections/lib/design-service/projects';
import { SharedFileDownloadTypes } from '@paragon/data-connections/lib/design-service/sharedComponentSets';
import { hasTrePreservePlateTypePermission } from '@paragon/login-tools';
import { getPlateManufacturer } from '@paragon/material-lists';
import { addDialog, enqueueError, enqueueWarning } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { calculateMemberPolygon } from '@paragon/three-shared';
import { assertExhaustiveSwitch, notNullish, pickBy } from '@paragon/utilities';

import { DownloadMode } from '../../components/dialogs/download/DownloadsDialog';
import { getSTLFromComponent } from '../../library/geometryUtility/STLExporter';
import { getAppMonitor } from '../../monitor';
import { RootState } from '..';
import { getExcavations } from '../selectors/canaryDownloadSelectors';
import { getRequestForAnalysisSetBeingViewed } from '../selectors/downloadSelectors';
import { getActiveAnalysisSet } from '../selectors/getActiveAnalysisSetSelector';
import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import { getAnalysisResultGuids } from '../selectors/getAnalysisResultsForActiveAnalysisSetSelector';
import { getCommittedComponentDesigns } from '../selectors/getComponentDesignsSelectors';
import { getContextCollectionName } from '../selectors/getContextCollectionName';
import { getProjectLayoutModel } from '../selectors/getProjectLayoutModel';
import { getSelectedAnalysisResult } from '../selectors/getSelectedAnalysisResultSelector';
import { getSelectedCheckResultsLoadCase } from '../selectors/getSelectedCheckResultsLoadCaseSelector';
import { getSingleSelectedItemEntryOrNull } from '../selectors/getSelectedItemSelector';
import { getAnalysisSetGuidToQuantity } from '../selectors/sharedComponentSets';
import { createDoWorkingChangesExistOnComponentDesign } from '../selectors/workingChangesSelectors';
import { calculateLayoutAutomaticDimensions } from './layout/layoutActions';
import { loadOrganizationSettingsIfNeeded } from './settingsActions';
import { AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';

/**
 * The server names the downloads it sends, following the organization's setting. A few files never reach it -- the STL
 * is built here, and the quality control files come back as JSON -- so those have to be named the same way here.
 */
function shouldAppendDateAndTimeToFileName(state: RootState) {
  return state.application.organizationSettings?.appendDateAndTimeToDownloadFileNames ?? false;
}

function getEmailDownloadDialogText(email: string) {
  return `You will receive an email when the requested download is ready at ${email}.`;
}

function constructStandardFileNameForDownload(
  state: RootState,
  options: {
    mode: DownloadMode;
    userVisibleDescription: string;
    extension: string;
  },
) {
  switch (options.mode) {
    case 'bulk':
    case 'custom':
      const contextName = getContextCollectionName(state);
      const customQuantities = options.mode === 'custom' ? ' Custom Quantities' : '';
      return sanitizeFilename(
        contextName
          ? `${contextName} ${options.userVisibleDescription}${customQuantities}.${options.extension}`
          : `${options.userVisibleDescription}${customQuantities}.${options.extension}`,
      );

    case 'single':
      const activeComponentDesign = getActiveComponentDesign(state);
      if (activeComponentDesign == null) throw new Error('No active component.');

      return sanitizeFilename(
        `${activeComponentDesign.component.name} ${options.userVisibleDescription}.${options.extension}`,
      );

    default:
      assertExhaustiveSwitch(options.mode);
  }
}

export const downloadTrussDesignDrawing =
  (downloadRequest: DownloadRequest, mode: DownloadMode, options: TrussDesignDrawingOptions): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    const request = getRequestForAnalysisSetBeingViewed(state)(downloadRequest);
    // Custom quantities list every component, with zero for the ones left out
    const requestedGuidToQuantity = pickBy(request.guidToQuantity, (quantity) => quantity !== 0);

    // A component with working changes has no analysis set representing its current state, so the server refuses to
    // draw any of them rather than drawing a stale one. Leave those components out instead.
    const componentDesignGuidsWithWorkingChanges =
      request.guidType === 'ComponentDesign'
        ? Object.keys(requestedGuidToQuantity).filter((componentDesignGuid) =>
            createDoWorkingChangesExistOnComponentDesign(componentDesignGuid)(state),
          )
        : [];
    const guidToQuantity = omit(requestedGuidToQuantity, componentDesignGuidsWithWorkingChanges);

    const requestedCount = Object.keys(requestedGuidToQuantity).length;
    const analyzedCount = Object.keys(guidToQuantity).length;
    if (requestedCount === 0) {
      dispatch(enqueueError('Cannot generate TDD because no components have a quantity greater than zero.'));
      return;
    }
    if (analyzedCount === 0) {
      dispatch(enqueueError('Cannot generate TDD because no components have been analyzed.'));
      return;
    }
    if (analyzedCount < requestedCount) {
      dispatch(
        enqueueWarning(
          `Some components (${requestedCount - analyzedCount} of ${requestedCount}) have not been analyzed and will ` +
            'not be included in the download.',
        ),
      );
    }

    const spinnerId = dispatch(addSpinnerMessage('Generating truss design drawing(s)...'));

    try {
      const projectLayoutModel = getProjectLayoutModel(state);
      const result = await designService.componentDesigns.downloadTrussDesignDrawings({
        ...options,
        ...request,
        guidToQuantity,
        projectGuid: projectLayoutModel?.guid ?? null,
      });

      const fileName = constructStandardFileNameForDownload(state, {
        mode,
        extension: 'pdf',
        userVisibleDescription: 'Truss Design Drawing',
      });

      result.download(fileName);
    } catch (error) {
      console.error(error);
      dispatch(
        enqueueError(getUserVisibleOrDefaultExceptionMessage(error, 'Error generating truss design drawing(s).')),
      );
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const downloadLayoutPdf =
  (selectedLevelGuid?: string, nameOverride?: string, levelName?: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    const automaticDimensionSegments = calculateLayoutAutomaticDimensions(
      selectedLevelGuid ?? null,
      Object.values(state.layout.trussEnvelopes),
      Object.values(state.layout.bearingEnvelopes),
    );

    if (state.application.openContext?.type === 'shared-component-set') {
      await dispatch(
        downloadSharedComponentSetFiles({
          fileType: 'LayoutPdf',
          sharedComponentSetLookupCode: state.application.openContext.sharedComponentSetLookupCode,
          selectedLevelGuid,
          nameOverride,
          automaticDimensionSegments,
          selectedLevelName: levelName,
        }),
      );
      return;
    }

    if (state.application.openContext?.type !== 'project') throw new Error('Invalid context for layout PDF download.');
    const projectGuid = state.application.openContext.projectGuid;

    const spinnerId = dispatch(addSpinnerMessage('Generating layout PDF...'));
    try {
      const response = await designService.projects.getLayoutPdf(
        projectGuid,
        automaticDimensionSegments,
        selectedLevelGuid,
        nameOverride,
        levelName,
      );
      response.download('Layout.pdf');
    } catch (e) {
      dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(e, 'Error generating layout PDF.')));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const downloadShopDrawings =
  (downloadRequest: DownloadRequest, mode: DownloadMode, combined: boolean): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    const spinnerMessage = 'Generating shop drawing(s)...';
    const spinnerId = dispatch(addSpinnerMessage(spinnerMessage));

    try {
      const loggedIn = Boolean(state.logInData.loggedInUser);
      const organizationSettings = await dispatch(loadOrganizationSettingsIfNeeded());
      const portraitOrientation = organizationSettings?.portraitOrientationShopDrawings ?? !combined;
      const options = {
        includeComponentSchematics: !combined,
        includeCutSheets: true,
        combineCutSheets: combined,

        portraitOrientation,
        distanceFormat:
          state.userSettings?.viewOptionSettings.distanceFormat ??
          organizationSettings?.useDistanceFormat ??
          'FeetInchesSixteenths',
        squareCutAngleIsZero: organizationSettings?.squareCutAngleIsZero ?? false,

        cutsOnSamePage: portraitOrientation,
        includedMemberTypes: ['Chord', 'Web', 'Block', 'Other'],
      } satisfies ShopDrawingOptions;

      const openContextType = state.application.openContext?.type;
      const result = await designService.componentDesigns.downloadShopDrawings({
        ...downloadRequest,
        options,
        allowEmail: loggedIn && openContextType !== 'station' && openContextType !== 'schedule-station',
      });

      if (result instanceof EmailResponse) {
        if (state.logInData.loggedInUser) {
          dispatch(addDialog(getEmailDownloadDialogText(state.logInData.loggedInUser.email)));
        }
      } else if (result instanceof NoFileContentResponse) {
        dispatch(enqueueWarning('No content available for download.'));
      } else {
        const fileName = constructStandardFileNameForDownload(state, {
          mode,
          extension: 'pdf',
          userVisibleDescription: 'Cut Sheet',
        });

        result.download(fileName);
      }
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('Cut sheet generation failed.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const generateDigitalQcFileForActiveComponentDesign =
  (tangiblePlateTypeMap: { [platePairGuid: string]: PlateManufacturer }): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    if (!state.selections.activeComponentDesignGuid) throw new Error('Could not find active component.');

    const spinnerId = dispatch(addSpinnerMessage('Generating Digital QC file for active component...'));

    const analysisSet = state.analysisSets.sets.find((set) => set.guid === state.analysisSets.activeAnalysisSetGuid);
    if (analysisSet === undefined) throw new Error('Could not find analysis set.');

    try {
      const activeComponentDesign = getActiveComponentDesign(state);
      const unstampedFileName = activeComponentDesign
        ? `${activeComponentDesign.component.name}.json`
        : 'qcinspection.json';
      const fileName = shouldAppendDateAndTimeToFileName(state)
        ? appendDateAndTimeToFileName(unstampedFileName)
        : unstampedFileName;

      // TODO: Once the other QC output format is implemented, add UI/Organization settings to select which version desired.
      const qcStandard: DigitalQCOutputFormat = 'Sbca_0_4_2';
      const forceReanalysis = false;

      const erroredPlatePairNames = await downloadDigitalQcFile(
        fileName,
        analysisSet.guid,
        getContextCollectionName(state) ?? '',
        qcStandard,
        forceReanalysis,
        tangiblePlateTypeMap,
        state.sharedComponentSet.sharedComponentSet?.lookupCode,
      );
      if (erroredPlatePairNames.length > 0) {
        dispatch(enqueueError(`Digital QC file generation failure(s) for plates: ${erroredPlatePairNames.join(', ')}`));
      }
      getAppMonitor().recordInteraction('Digital QC file downloaded', {
        usingSharedComponentSet: getState().application.openContext?.type === 'shared-component-set',
      });
    } catch (error) {
      dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(error, 'Digital QC file generation failed.')));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const generateQualityControlPdfForActiveComponentDesign =
  (jsiMinimumOrPlatePairGuids: number | string[]): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    if (!state.selections.activeComponentDesignGuid) throw new Error('Could not find active component.');

    const spinnerId = dispatch(addSpinnerMessage('Generating quality control diagrams for active component...'));

    const analysisSet = state.analysisSets.sets.find((set) => set.guid === state.analysisSets.activeAnalysisSetGuid);
    if (analysisSet === undefined) throw new Error('Could not find analysis set.');

    try {
      const activeComponentDesign = getActiveComponentDesign(state);
      const unstampedFileName = activeComponentDesign
        ? `${activeComponentDesign.component.name} - QC Diagrams.pdf`
        : 'QC Diagrams.pdf';
      const fileName = shouldAppendDateAndTimeToFileName(state)
        ? appendDateAndTimeToFileName(unstampedFileName)
        : unstampedFileName;

      const forceReanalysis = false;
      const erroredPlatePairNames = await downloadQualityControlDiagrams(
        fileName,
        analysisSet.guid,
        Array.isArray(jsiMinimumOrPlatePairGuids) ? null : jsiMinimumOrPlatePairGuids,
        Array.isArray(jsiMinimumOrPlatePairGuids) ? jsiMinimumOrPlatePairGuids : null,
        getContextCollectionName(state) ?? '',
        forceReanalysis,
        state.sharedComponentSet.sharedComponentSet?.lookupCode,
      );
      if (erroredPlatePairNames.length > 0) {
        dispatch(
          enqueueError(`Quality control diagram generation failure(s) for plates: ${erroredPlatePairNames.join(', ')}`),
        );
      }
      getAppMonitor().recordInteraction('Quality control diagram file downloaded', {
        usingSharedComponentSet: getState().application.openContext?.type === 'shared-component-set',
      });
    } catch (error) {
      dispatch(
        enqueueError(getUserVisibleOrDefaultExceptionMessage(error, 'Quality control diagram generation failed.')),
      );
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const generateDxfForActiveComponentDesign = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();

  if (!state.analysisSets.activeAnalysisSetGuid || state.analysisSets.sets.length < 1) {
    dispatch(enqueueError('Cannot generate DXF because the truss has not been analyzed.'));
    return;
  }

  const analysisSet = state.analysisSets.sets.find((set) => set.guid === state.analysisSets.activeAnalysisSetGuid);
  if (analysisSet === undefined) throw new Error('Could not find analysis set.');
  const { analysisReportGuid } = analysisSet;

  const spinnerId = dispatch(addSpinnerMessage('Generating DXF for active component...'));
  try {
    const dxfResult = await designService.componentDesigns.getDxf(analysisReportGuid);
    const activeComponentDesign = getActiveComponentDesign(state);
    const fallBackFileName = activeComponentDesign ? `${activeComponentDesign.component.name}.dxf` : 'DXF.dxf';

    dxfResult.download(sanitizeFilename(fallBackFileName));
  } catch {
    dispatch(enqueueError('DXF generation failed.'));
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};

export const exportActiveLayoutToIfcFile =
  (selectedLevelGuid?: string, nameOverride?: string) => (): AsyncThunkActionVoid => async (dispatch, getState) => {
    const state = getState();

    if (state.application.openContext?.type === 'shared-component-set') {
      await dispatch(
        downloadSharedComponentSetFiles({
          fileType: 'LayoutIfc',
          sharedComponentSetLookupCode: state.application.openContext.sharedComponentSetLookupCode,
          selectedLevelGuid,
          nameOverride,
        }),
      );
      return;
    }

    const { layout } = state;
    if (!layout) return;

    const spinnerId = dispatch(addSpinnerMessage('Generating IFC file...'));

    const project = getProjectLayoutModel(state)!;

    try {
      const file = await designService.projects.downloadLayoutIfc(
        project.guid,
        nameOverride ?? project.name,
        selectedLevelGuid,
      );
      // The server names this file, applying any date and time suffix; this is only a fallback
      file.download(sanitizeFilename(`${nameOverride ?? project.name}.ifc`));
    } catch (error) {
      dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(error, 'Error exporting layout to IFC.')));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const downloadStlForActiveComponentDesign = (): ThunkActionVoid => (dispatch, getState) => {
  const componentDesign = getActiveComponentDesign(getState());
  if (!componentDesign) {
    dispatch(enqueueError('A component must be selected.'));
    return;
  }
  const { component } = componentDesign;
  const memberGeometryInfos = component.members
    .map((member) => ({
      polygon: calculateMemberPolygon(member, component)!,
      thickness: member.thickness,
    }))
    .filter((info) => notNullish(info.polygon));
  const fileContents = getSTLFromComponent(memberGeometryInfos);
  const fileName = 'ComponentModel.stl';

  saveTextContent(
    shouldAppendDateAndTimeToFileName(getState()) ? appendDateAndTimeToFileName(fileName) : fileName,
    fileContents,
  );
};

export function downloadMachineryTrs(downloadMachineryTrsRequest: DownloadMachineryTrsRequest): AsyncThunkActionVoid {
  return async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Downloading Machinery TRS files...'));

    try {
      const fileResponse = await designService.componentDesigns.downloadMachineryTrs(downloadMachineryTrsRequest);
      if (fileResponse instanceof NoFileContentResponse) {
        dispatch(enqueueWarning('The chosen Machinery TRS conditions did not produce any results.'));
      } else {
        fileResponse.download();
      }
    } catch (error) {
      dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(error, 'Error downloading machinery TRS.')));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

function warnForPlatePairInTreDownload(platePair: PlatePair) {
  const plateManufacturer = getPlateManufacturer(platePair.plateType);
  return plateManufacturer !== 'Any' && plateManufacturer !== 'MiTek';
}

export const downloadConvertedFileType =
  (
    downloadRequest: DownloadRequest,
    extension: Exclude<ConvertibleComponentFileExtension, 'trs'>,
    showSpinner: boolean,
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { sharedComponentSet } = getState().sharedComponentSet;
    if (getState().application.openContext?.type === 'shared-component-set' && !sharedComponentSet) {
      dispatch(enqueueError('Wait for shared component set to load before downloading files.'));
      return;
    }

    if (extension === 'tre' && !hasTrePreservePlateTypePermission(getState())) {
      const guids = Object.keys(downloadRequest.guidToQuantity);
      let componentDesigns: ComponentDesign[];
      switch (downloadRequest.guidType) {
        case 'ComponentDesign':
          componentDesigns = getCommittedComponentDesigns(getState()).filter((componentDesign) =>
            guids.includes(componentDesign.guid),
          );
          break;
        case 'AnalysisSet':
          componentDesigns = getState()
            .analysisSets.sets.filter((analysisSet) => guids.includes(analysisSet.guid))
            .map(({ componentDesign }) => componentDesign);
          break;
        default:
          throw new Error('Invalid guidType');
      }
      const showTrePlateWarning = componentDesigns.some((componentDesign) =>
        componentDesign.component.platePairs.some(warnForPlatePairInTreDownload),
      );
      if (showTrePlateWarning) {
        dispatch(enqueueWarning('Plates are being converted to MiTek plates in TRE file(s)'));
      }
    }

    const spinnerId = showSpinner ? dispatch(addSpinnerMessage('Downloading component files...')) : null;

    function setFileResponseForSavedComponent() {
      switch (extension) {
        case 'blade':
          return designService.componentDesigns.downloadBlade(downloadRequest);

        case 'bvn':
          return designService.componentDesigns.downloadBvn(downloadRequest);

        case 'cyb':
          const selectedEntry = getSingleSelectedItemEntryOrNull(getState());
          const selectedMemberGuid =
            selectedEntry?.type === 'member' || selectedEntry?.type === 'brace' ? selectedEntry.item.guid : undefined;
          return designService.componentDesigns.downloadCyb(downloadRequest, selectedMemberGuid);

        case 'ifc':
          return designService.componentDesigns.downloadComponentDesignIfc(downloadRequest);

        case 'pcd':
          return designService.componentDesigns.downloadPcd(downloadRequest);

        case 'tps':
          return designService.componentDesigns.downloadTps(downloadRequest);

        case 'tre':
          return designService.componentDesigns.downloadTre(downloadRequest);

        case 'omn':
          return designService.componentDesigns.downloadOmn(downloadRequest);

        case 'steelTrs':
        case 'trussDAndE': {
          const project = getProjectLayoutModel(getState());
          const requestWithProject = project
            ? { ...downloadRequest, guidToProjectGuid: mapValues(downloadRequest.guidToQuantity, () => project.guid) }
            : downloadRequest;
          return extension === 'steelTrs'
            ? designService.componentDesigns.downloadSteelTrs(requestWithProject)
            : designService.componentDesigns.downloadTrussDAndE(requestWithProject);
        }

        default:
          return assertExhaustiveSwitch(extension, 'Unrecognized component file extension');
      }
    }

    try {
      const fileResponse = await setFileResponseForSavedComponent();
      fileResponse.download();
    } catch (error) {
      dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(error, 'Error converting file(s).')));
    } finally {
      if (spinnerId !== null) dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const downloadAnalysisReportPdf = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();

  const activeAnalysisSet = getActiveAnalysisSet(state);
  const analysisResults = activeAnalysisSet && getAnalysisResultGuids(activeAnalysisSet);

  if (analysisResults == null || analysisResults.length === 0) {
    dispatch(enqueueError('To download an Analysis Report PDF, you need to first run a detailed analysis.'));
    return;
  }

  const spinnerId = dispatch(addSpinnerMessage('Downloading Analysis Report PDF...'));
  try {
    const fileResult = await designService.analysisPdfs.getByAnalysisSet(activeAnalysisSet!.guid);
    fileResult.download();
  } catch (exception) {
    const message = getUserVisibleOrDefaultExceptionMessage(exception, 'Error generating Analysis Report PDF.');
    dispatch(enqueueError(message));
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};

export const downloadLoadCaseCsv = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();
  const { activeAnalysisSetGuid } = state.analysisSets;
  const activeAnalysisResult = getSelectedAnalysisResult(state);
  const activeComponentDesign = getActiveComponentDesign(state);
  const activeLoadCase = getSelectedCheckResultsLoadCase(state);

  if (!activeAnalysisSetGuid || !activeAnalysisResult || !activeComponentDesign || !activeLoadCase) {
    dispatch(
      enqueueError(
        'To download a Load Case CSV, you need to first run a detailed analysis, and then select a load case from the check results panel.',
      ),
    );
    return;
  }

  const spinnerId = dispatch(addSpinnerMessage('Downloading Load Case CSV...'));
  try {
    const fileResult = await designService.analysisCsvs.pGetByAnalysisSetAndAnalysisResult(
      activeAnalysisSetGuid,
      activeAnalysisResult.guid,
    );
    fileResult.download(`${activeComponentDesign.component.name}_${activeLoadCase.name}.csv`);
  } catch (exception) {
    const message = getUserVisibleOrDefaultExceptionMessage(exception, 'Error generating Load Case CSV.');
    dispatch(enqueueError(message));
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};

export const downloadCanaryCsv = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const spinnerId = dispatch(addSpinnerMessage('Downloading Canary CSV...'));
  try {
    const fileResult = await designService.componentDesigns.downloadCanaryCsv(getExcavations(getState()));
    fileResult.download('canaries.csv');
  } catch (exception) {
    const message = getUserVisibleOrDefaultExceptionMessage(exception, 'Error downloading Canary CSV.');
    dispatch(enqueueError(message));
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};

const overLengthPrice = 9999;
export const downloadReport =
  (
    downloadRequest: DownloadRequest,
    report: 'Lumber Pick List' | 'Plate Pick List' | 'Material Report CSV' | 'Material Report PDF' | 'Component Report',
    showSpinner: boolean,
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { sharedComponentSet } = getState().sharedComponentSet;
    if (getState().application.openContext?.type === 'shared-component-set' && !sharedComponentSet) {
      dispatch(enqueueError('Wait for shared component set to load before downloading files.'));
      return;
    }

    const spinnerMessage = `Downloading ${report}...`;
    const spinnerId = showSpinner ? dispatch(addSpinnerMessage(spinnerMessage)) : null;

    try {
      let fileResult: DownloadableFileResponse;
      switch (report) {
        case 'Lumber Pick List':
          fileResult = await designService.componentDesigns.downloadLumberPickList(downloadRequest);
          break;

        case 'Plate Pick List':
          fileResult = await designService.componentDesigns.downloadPlatePickList(downloadRequest);
          break;

        case 'Material Report CSV':
        case 'Material Report PDF':
          const downloadMaterialReportRequest: DownloadMaterialReportRequest = { ...downloadRequest, overLengthPrice };
          fileResult =
            report === 'Material Report CSV'
              ? await designService.componentDesigns.downloadMaterialReportCsv(downloadMaterialReportRequest)
              : await designService.componentDesigns.downloadMaterialReportPdf(downloadMaterialReportRequest);
          break;

        case 'Component Report':
          fileResult = await designService.componentDesigns.downloadComponentReport(downloadRequest);
          break;

        default:
          assertExhaustiveSwitch(report);
      }
      fileResult.download();
    } catch (error) {
      dispatch(enqueueError(`Error downloading ${report}. Contact help@paragontruss.com.`));
      console.error(error);
    } finally {
      if (spinnerId !== null) dispatch(removeSpinnerMessage(spinnerId));
    }
  };

/**
 * Only layout files are downloaded through the Shared Component Set routes; component downloads go through the public
 * component design download routes with the set's lookup code in the request.
 */
export type DownloadSharedComponentSetFilesArgs = {
  fileType: SharedFileDownloadTypes;
  sharedComponentSetLookupCode?: string;
  selectedLevelGuid?: string;
  nameOverride?: string;
  automaticDimensionSegments?: SegmentForLevel[];
  selectedLevelName?: string;
};

export const downloadSharedComponentSetFiles =
  (args: DownloadSharedComponentSetFilesArgs): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { fileType, sharedComponentSetLookupCode } = args;
    const { sharedComponentSet } = getState().sharedComponentSet;

    if (!sharedComponentSet && !sharedComponentSetLookupCode)
      throw new Error('No SharedComponentSet found when downloading files');

    const spinnerMessage = `Downloading files...`;
    const spinnerId = dispatch(addSpinnerMessage(spinnerMessage));

    const params = new URLSearchParams();
    if (args.selectedLevelGuid) params.append('selectedLevelGuid', args.selectedLevelGuid);
    if (args.nameOverride) params.append('nameOverride', args.nameOverride);
    if (args.selectedLevelName) params.append('selectedLevelName', args.selectedLevelName);

    try {
      const fileResult = await designService.sharedComponentSets.downloadFiles(
        sharedComponentSetLookupCode ?? sharedComponentSet!.lookupCode,
        fileType,
        params,
        args.automaticDimensionSegments,
      );
      fileResult.download();
    } catch (error) {
      dispatch(enqueueError(`Error downloading files. Contact help@paragontruss.com.`));
      console.error(error);
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

/**
 * Draws the components of a Shared Component Set which is not necessarily the one open in the app, which is why this
 * builds its own request rather than using the download request selectors.
 */
export const downloadTrussDesignDrawingsForSharedComponentSet =
  (sharedComponentSet: SharedComponentSet): AsyncThunkActionVoid =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Generating truss design drawing(s)...'));

    try {
      const result = await designService.componentDesigns.downloadTrussDesignDrawings({
        ...DEFAULT_TRUSS_DESIGN_DRAWING_OPTIONS,
        guidType: 'AnalysisSet',
        guidToQuantity: getAnalysisSetGuidToQuantity(sharedComponentSet),
        groupName: sharedComponentSet.name,
        sharedComponentSetLookupCode: sharedComponentSet.lookupCode,
        mergeIntoOnePdf: true,
        projectGuid: null,
      });

      result.download();
    } catch (error) {
      console.error(error);
      dispatch(
        enqueueError(getUserVisibleOrDefaultExceptionMessage(error, 'Error generating truss design drawing(s).')),
      );
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const downloadPlatePriceComparisonCsv =
  (request: ComparePlateListsRequest): AsyncThunkActionVoid =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Downloading Plate Price Comparison CSV...'));
    try {
      const fileResult = await designService.componentDesigns.comparePlateLists(request);
      fileResult.download();
    } catch (exception) {
      const message = getUserVisibleOrDefaultExceptionMessage(
        exception,
        'Error generating Plate Price Comparison CSV.',
      );
      dispatch(enqueueError(message));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
